import { Injectable, DOCUMENT, inject } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AccessibilityObserverService {
  private document = inject<Document>(DOCUMENT);

  private observer!: MutationObserver | undefined;
  private landmarkCounter = 0;
  private started = false;
  private processTimeout: any;

  public start(): void {
    // Guard: to run only once
    if (this.started) return;

    this.started = true;
    const body = this.document.body as HTMLElement;

    // Apply fixes to the current DOM which is already loaded.
    this.applyFixesToTree(body);

    // Begin watching for future DOM changes to aplly fixes.
    this.initObserver();
  }

  private applyFixesToTree(root: HTMLElement): void {
    this.applyWcagFixes(root);
    root.querySelectorAll('*').forEach(el => {
      if (el instanceof HTMLElement) {
        this.applyWcagFixes(el);
      }
    });
  }

  // Disconnect the observer to stop receiving mutations
  public stop(): void {
    if (this.observer) {
      try {
        this.observer.disconnect();
      } finally {
        this.observer = undefined;
      }
    }

    // Clear debounced work
    if (this.processTimeout) {
      clearTimeout(this.processTimeout);
      this.processTimeout = undefined;
    }

    // Reset started flag
    this.started = false;
  }

  // this will catch and fix elements added or changed after initial load
  private initObserver(): void {
    this.observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node instanceof HTMLElement) {
              this.applyWcagFixes(node);
              node.querySelectorAll('*').forEach(child => {
                if (child instanceof HTMLElement) {
                  this.applyWcagFixes(child);
                }
              });
            }
          });
        } else if (
          mutation.type === 'attributes' &&
          mutation.target instanceof HTMLElement &&
          mutation.attributeName
        ) {
          this.handleAttributeMutation(mutation.target, mutation.attributeName);
        }
      }

      setTimeout(() => {
        this.fixEmptyOrGenericTableHeaders(this.document.body);
      }, 200);
    });

    this.observer.observe(this.document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'aria-activedescendant', 'aria-expanded']
    });
  }

  private handleAttributeMutation(target: HTMLElement, attr: string) {
    switch (attr) {
      case 'aria-hidden':
        this.fixAriaHiddenFocusable(target);
        break;

      case 'aria-activedescendant':
        this.fixInvalidAriaActivedescendant(target);
        break;

      case 'aria-expanded':
        this.fixInvalidAriaExpanded(target);
        break;
    }
  }

  private applyWcagFixes(element: HTMLElement): void {
    this.fixHeadingOrder(element);
    this.fixTextAlternatives(element);
    this.fixPrimeNgComponents(element);
    this.fixGenericInputs(element);
    this.fixDropdownRoles(element);
    this.fixInvalidAriaRequired(element);
    this.fixScrollableContainers(element);
    this.ensureUniqueNavigationLandmarks(element);
    this.fixMissingLandmarks(element);
    this.fixInvalidAriaSelected(element);
    this.fixInvalidAriaRequired(element);
    this.fixSidebarRoles();
    this.ensureChoicesItemRoles(element);
    this.preferAriaLabelForToggleableRegions(element);
  }

  // fixed prime-ng components
  private fixPrimeNgComponents(el: HTMLElement): void {
    // p-radioButton
    if (el.classList.contains('p-radiobutton')) {
      const input = el.querySelector('input[type="radio"]');
      const labelText = el.getAttribute('data-label') || 'radio option';

      if (input) {
        if (!input.hasAttribute('aria-label')) {
          input.setAttribute('aria-label', labelText);
        }
        if (!input.hasAttribute('role')) {
          input.setAttribute('role', 'radio');
        }
        if (!input.hasAttribute('aria-checked')) {
          input.setAttribute(
            'aria-checked',
            (input as HTMLInputElement).checked ? 'true' : 'false'
          );
        }

        input.addEventListener('change', () => {
          input.setAttribute(
            'aria-checked',
            (input as HTMLInputElement).checked ? 'true' : 'false'
          );
        });
      }

      if (
        !el.hasAttribute('tabindex') &&
        !el.classList.contains('p-disabled') &&
        !(input as HTMLInputElement)?.disabled
      ) {
        el.setAttribute('tabindex', '0');
      }
    }

    // p-checkbox
    if (el.classList.contains('p-checkbox')) {
      const box = el.querySelector('.p-checkbox-box');
      const input = el.querySelector('input[type="checkbox"]');
      const parentInteractive = el.closest(
        '[role="option"], [role="row"], [role="button"], [role="link"]'
      );
      const isNestedInInteractive = parentInteractive && parentInteractive !== el;
      if (box) {
        if (!isNestedInInteractive) {
          if (!box.hasAttribute('role')) {
            box.setAttribute('role', 'checkbox');
          }
          if (!box.hasAttribute('tabindex')) {
            box.setAttribute('tabindex', '0');
          }
          if (!box.hasAttribute('aria-checked')) {
            box.setAttribute(
              'aria-checked',
              (input as HTMLInputElement)?.checked ? 'true' : 'false'
            );
          }
        } else {
          box.removeAttribute('role');
          box.removeAttribute('tabindex');
          box.removeAttribute('aria-checked');
        }
        if (!box.hasAttribute('aria-label')) {
          const label = input?.getAttribute('aria-label') || 'checkbox option';
          box.setAttribute('aria-label', label);
        }
      }
    }

    //p-multislect
    if (el.classList.contains('p-multiselect')) {
      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'combobox');
      }
      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'listbox');
      }
      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'Filter by routing type');
      }
    }

    // p-calendar
    if (el.classList.contains('p-calendar')) {
      const input = el.querySelector('input[type="text"]');
      const popup = el.querySelector('.p-datepicker');

      if (input) {
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'dialog');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-label', 'Date picker');
        input.setAttribute('placeholder', 'Select date');

        if (popup && !popup.id) {
          popup.id = 'calendar-panel-' + Math.random().toString(36).substring(2, 8);
        }

        if (popup?.id) {
          input.setAttribute('aria-controls', popup.id);
        }
      }
    }

    // p-autocomplete
    if (el.classList.contains('p-autocomplete')) {
      const input = el.querySelector('input[type="text"]');
      if (input) {
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'listbox');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-label', 'Search suggestions');
      }
    }

    // p-datepicker (if used separately from p-calendar)
    if (el.classList.contains('p-datepicker')) {
      const input = el.querySelector('input[type="text"]');
      if (input) {
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'dialog');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-label', 'Date picker');
      }
    }

    // p-dropdown
    if (el.classList.contains('p-dropdown')) {
      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'combobox');
      }
      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'listbox');
      }
      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'dropdown menu');
      }
    }

    // p-dropdown used in paginator
    if (el.classList.contains('p-dropdown') && el.classList.contains('p-paginator-rpp-options')) {
      const input = el.querySelector('input[type="text"]');
      const trigger = el.querySelector('.p-dropdown-trigger');
      const label = el.querySelector('.p-dropdown-label');

      if (input) {
        if (!input.hasAttribute('role')) {
          input.setAttribute('role', 'combobox');
        }
        if (!input.hasAttribute('aria-haspopup')) {
          input.setAttribute('aria-haspopup', 'listbox');
        }
        if (!input.hasAttribute('aria-expanded')) {
          input.setAttribute('aria-expanded', 'false');
        }
        if (!input.hasAttribute('aria-label')) {
          input.setAttribute('aria-label', 'Items per page selector');
        }
        if (!input.hasAttribute('disabled') && el.classList.contains('p-disabled')) {
          input.setAttribute('disabled', '');
        }
      }

      if (trigger && !trigger.hasAttribute('aria-label')) {
        trigger.setAttribute('aria-label', 'Open items per page dropdown');
      }

      if (label && !label.hasAttribute('aria-label')) {
        label.setAttribute('aria-label', 'Selected items per page');
      }
    }

    // p-paginator navigation buttons
    if (el.tagName === 'BUTTON' && el.classList.contains('p-paginator-element')) {
      const labelMap = {
        'p-paginator-first': 'First page',
        'p-paginator-prev': 'Previous page',
        'p-paginator-next': 'Next page',
        'p-paginator-last': 'Last page'
      };
      type LabelMapKey = keyof typeof labelMap;
      for (const cls in labelMap) {
        if (el.classList.contains(cls) && !el.hasAttribute('aria-label')) {
          el.setAttribute('aria-label', labelMap[cls as LabelMapKey]);
        }
      }

      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'button');
      }

      if (!el.hasAttribute('aria-disabled')) {
        const isDisabled = el.hasAttribute('disabled') || el.classList.contains('p-disabled');
        el.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
      }

      if (!el.hasAttribute('tabindex') && !el.hasAttribute('disabled')) {
        el.setAttribute('tabindex', '0');
      }
    }

    // readonly input[type="text"]
    if (
      el.tagName === 'INPUT' &&
      el.getAttribute('type') === 'text' &&
      el.getAttribute('readonly') === 'readonly'
    ) {
      el.setAttribute('role', 'combobox');

      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'listbox');
      }
      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'Selection input');
      }
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }

      if (!el.hasAttribute('aria-controls')) {
        const isMultiSelect = !!el.closest('.p-multiselect');
        const panelSelector = isMultiSelect
          ? '.p-multiselect-panel ul[role="listbox"]'
          : '.p-dropdown-panel ul[role="listbox"], .p-autocomplete-panel ul[role="listbox"]';

        let listbox = this.document.querySelector(panelSelector) as HTMLElement;

        if (!listbox) {
          listbox = this.document.querySelector('ul[role="listbox"]') as HTMLElement;
        }

        if (listbox) {
          const listboxId = listbox.id || `listbox-${Math.random().toString(36).substring(2, 8)}`;
          listbox.id = listboxId;

          el.setAttribute('aria-controls', listboxId);
        } else {
          const fallbackId = `listbox-fallback-${el.id}`;
          el.setAttribute('aria-controls', fallbackId);
        }
      }
    }

    // p-splitbutton menu button
    if (el.tagName === 'BUTTON' && el.classList.contains('p-splitbutton-menubutton')) {
      if (el.hasAttribute('aria-aria-haspopup')) {
        el.removeAttribute('aria-aria-haspopup');
      }

      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'menu');
      }

      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }

      if (!el.hasAttribute('aria-label') || el.getAttribute('aria-label') === 'action') {
        el.setAttribute('aria-label', 'Open split button menu');
      }

      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'button');
      }

      if (!el.hasAttribute('aria-disabled')) {
        const isDisabled = el.hasAttribute('disabled') || el.classList.contains('p-disabled');
        el.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
      }

      if (!el.hasAttribute('tabindex') && !el.hasAttribute('disabled')) {
        el.setAttribute('tabindex', '0');
      }
    }

    // p-table / p-datatable
    if (el.classList.contains('p-table') || el.classList.contains('p-datatable')) {
      const table = el.querySelector('table');
      if (table && !table.hasAttribute('role')) {
        table.setAttribute('role', 'table');
      }
      const headers = table?.querySelectorAll('th');
      headers?.forEach(th => {
        if (!th.hasAttribute('scope')) {
          th.setAttribute('scope', 'col');
        }
      });
    }

    // p-inputnumber fix (spinbutton role and aria-valuenow="null" fix)
    if (el.classList.contains('p-inputnumber-input') && el.tagName === 'INPUT') {
      const inputElement = el as HTMLInputElement;
      if (!inputElement.hasAttribute('role')) inputElement.setAttribute('role', 'spinbutton');
      const val = inputElement.getAttribute('aria-valuenow');
      if (val === 'null' || val === '') {
        inputElement.setAttribute('aria-valuenow', inputElement.value || '0');
      }
    }

    // p-button
    if (el.classList.contains('p-button') && el.tagName === 'BUTTON') {
      const label = el.getAttribute('ng-reflect-label');
      if (!el.hasAttribute('aria-label') && label) {
        el.setAttribute('aria-label', label);
      }
    }

    if (el.classList.contains('p-button-white') && el.getAttribute('role') === 'checkbox') {
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'Toggle option');
      }
      if (!el.hasAttribute('title')) {
        el.setAttribute('title', 'Toggle option');
      }
    }

    // p-inputswitch
    if (el.tagName === 'P-INPUTSWITCH' || el.classList.contains('p-inputswitch')) {
      const input = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;

      if (input) {
        if (!input.hasAttribute('role')) {
          input.setAttribute('role', 'switch');
        }

        if (!input.hasAttribute('aria-checked')) {
          input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
        }

        if (!input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby')) {
          input.setAttribute('aria-label', 'Toggle option');
        }

        input.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === ' ' || event.key === 'Enter') {
            input.click();
            input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
            event.preventDefault();
          }
        });
      }
    }

    // p-multiselect listbox
    if (el.tagName === 'UL' && el.getAttribute('role') === 'listbox') {
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        el.setAttribute('aria-label', 'Available options');
      }
    }

    // Fix listbox child roles
    if (el.tagName === 'LI' && el.closest('ul[role="listbox"]')) {
      if (el.classList.contains('p-multiselect-item') && !el.hasAttribute('role')) {
        el.setAttribute('role', 'option');
      }
      if (el.classList.contains('p-multiselect-item-group') && !el.hasAttribute('role')) {
        el.setAttribute('role', 'group');
      }
      if (
        el.classList.contains('p-dropdown-empty-message') ||
        el.classList.contains('p-multiselect-empty-message')
      ) {
        el.setAttribute('role', 'option');
        el.setAttribute('aria-disabled', 'true');
        el.setAttribute('tabindex', '-1');
      }
      if (
        el.classList.contains('p-dropdown-item') &&
        (!el.textContent?.trim() || el.getAttribute('aria-label') === '')
      ) {
        el.setAttribute('aria-label', 'Option');
      }
    }

    // p-tabpanel
    if (el.tagName === 'DL') {
      const childDLs = el.querySelectorAll(':scope > dl');
      childDLs.forEach(child => {
        const wrapperDD = document.createElement('dd');
        el.insertBefore(wrapperDD, child);
        wrapperDD.appendChild(child);
      });
    }

    // p-overlayPanel Dialog + p-confirmDialog Alert Dialog fix
    if (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog') {
      if (!el.hasAttribute('aria-modal')) {
        el.setAttribute('aria-modal', 'true');
      }

      const hasLabel = el.hasAttribute('aria-label');
      const hasLabelledBy = el.hasAttribute('aria-labelledby');

      if (!hasLabel && !hasLabelledBy) {
        const heading = el.querySelector('h1, h2, h3, .p-dialog-title');
        if (heading) {
          const headingId =
            heading.id || `dialog-heading-${Math.random().toString(36).substring(2, 8)}`;
          heading.id = headingId;
          el.setAttribute('aria-labelledby', headingId);
        } else {
          el.setAttribute('aria-label', 'Confirmation Dialog');
        }
      }

      if (!el.hasAttribute('title')) {
        el.setAttribute('title', 'Confirmation Dialog');
      }
    }

    if (el.tagName === 'P-PROGRESSSPINNER' || el.classList.contains('p-progress-spinner')) {
      const root = el.classList.contains('p-progress-spinner')
        ? el
        : (el.querySelector('.p-progress-spinner[role="progressbar"]') as HTMLElement);

      if (root) {
        const hasName =
          (root.hasAttribute('aria-label') && root.getAttribute('aria-label')!.trim() !== '') ||
          (root.hasAttribute('aria-labelledby') &&
            root.getAttribute('aria-labelledby')!.trim() !== '') ||
          (root.hasAttribute('title') && root.getAttribute('title')!.trim() !== '');

        if (!hasName) {
          let inferred = 'Loading';
          const labelledByCandidate = root
            .closest('.field, .card, section')
            ?.querySelector('h1,h2,h3,h4,h5,h6,.p-card-title,.section-title');
          if (labelledByCandidate) {
            const lblId = labelledByCandidate.id || `lbl-${Math.random().toString(36).slice(2, 8)}`;
            labelledByCandidate.id = lblId;
            root.setAttribute('aria-labelledby', lblId);
          } else {
            root.setAttribute('aria-label', 'Loading data');
          }

          if (!root.hasAttribute('title')) {
            root.setAttribute('title', root.getAttribute('aria-label') || 'Loading data');
          }
        }

        if (!root.hasAttribute('aria-busy')) {
          root.setAttribute('aria-busy', 'true');
        }
      }
    }
  }

  // fixed roles for class choices__item
  //This method is only for form.io fixes
  private ensureChoicesItemRoles(el: HTMLElement): void {
    const isChoicesItem =
      el.classList.contains('choices__item') && el.classList.contains('choices__item--selectable');
    if (!isChoicesItem) return;

    if (el.getAttribute('role') !== 'option') {
      el.setAttribute('role', 'option');
    }

    const parentList = el.parentElement;
    if (parentList && parentList.getAttribute('role') !== 'listbox') {
      parentList.setAttribute('role', 'listbox');
      if (!parentList.hasAttribute('aria-label') && !parentList.hasAttribute('aria-labelledby')) {
        parentList.setAttribute('aria-label', 'Selected values');
      }
    }

    const val = (el.getAttribute('aria-selected') || '').trim();
    if (val !== 'true' && val !== 'false') {
      if (val) el.setAttribute('aria-selected', 'true');
      else el.setAttribute('aria-selected', 'false');
    }
  }

  // fixed duplicate landmark for toggleable regions
  private preferAriaLabelForToggleableRegions(el: HTMLElement): void {
    if (!el.matches('.p-toggleable-content[role="region"]')) return;

    const labelledby = (el.getAttribute('aria-labelledby') || '').trim();
    const ariaLabel = (el.getAttribute('aria-label') || '').trim();

    if (labelledby) {
      el.removeAttribute('aria-labelledby');
      el.setAttribute('aria-label', `Toggleable content ${++this.landmarkCounter}`);
    }
  }

  // validated aria-selected
  private fixInvalidAriaSelected(root: HTMLElement): void {
    const withAriaSelected = root.querySelectorAll<HTMLElement>('[aria-selected]');
    withAriaSelected.forEach(el => {
      const role = el.getAttribute('role') || '';
      const value = (el.getAttribute('aria-selected') || '').trim();
      const allowed = ['option', 'gridcell', 'row', 'tab', 'treeitem'];

      if (!allowed.includes(role)) {
        const isChoicesItem = el.classList.contains('choices__item');
        const choicesContainer =
          el.closest('.choices') || el.closest('.choices__list') || el.closest('.choices__inner');

        if (isChoicesItem && choicesContainer) {
          if (role !== 'option') el.setAttribute('role', 'option');

          const parentList = el.parentElement;
          if (parentList && parentList.getAttribute('role') !== 'listbox') {
            parentList.setAttribute('role', 'listbox');
            if (
              !parentList.hasAttribute('aria-label') &&
              !parentList.hasAttribute('aria-labelledby')
            ) {
              parentList.setAttribute('aria-label', 'Selected values');
            }
          }

          if (value !== 'true' && value !== 'false') {
            el.setAttribute('aria-selected', value ? 'true' : 'false');
          }
        } else {
          el.removeAttribute('aria-selected');
        }
      } else {
        if (value !== 'true' && value !== 'false') {
          el.setAttribute('aria-selected', value ? 'true' : 'false');
        }
      }
    });
  }

  // validated aria-expanded
  private fixInvalidAriaExpanded(el: HTMLElement): void {
    if (el.hasAttribute('aria-expanded')) {
      const role = el.getAttribute('role') || '';
      const allowedRoles = ['button', 'link', 'combobox', 'menuitem', 'treeitem'];
      if (!allowedRoles.includes(role)) {
        el.removeAttribute('aria-expanded');
      }
    }
  }

  // fixed sidebar roles
  private fixSidebarRoles(): void {
    const selector = [
      '.p-sidebar',
      '[data-pc-name="sidebar"]',
      '[role="complementary"].p-sidebar',
      '.p-sidebar.p-sidebar-active',
      '.sidebar.p-sidebar'
    ].join(',');

    const sidebars = this.document.querySelectorAll<HTMLElement>(selector);

    sidebars.forEach(sidebar => {
      if (!sidebar.isConnected) return;

      const role = sidebar.getAttribute('role') || '';
      const ariaModal = sidebar.getAttribute('aria-modal');
      if (ariaModal !== null && role !== 'dialog' && role !== 'alertdialog') {
        sidebar.removeAttribute('aria-modal');
      }

      const nestedWithin = sidebar.closest<HTMLElement>(
        '[role="main"], main,' +
          ' [role="region"], section[role="region"],' +
          ' [role="navigation"], nav,' +
          ' [role="banner"], header,' +
          ' [role="contentinfo"], footer'
      );

      if (role === 'complementary' && nestedWithin && nestedWithin !== sidebar) {
        const looksLikeNav = !!sidebar.querySelector(
          'nav, .p-menubar, .p-menu, .p-tieredmenu, .p-tabmenu, a'
        );
        sidebar.setAttribute('role', looksLikeNav ? 'navigation' : 'region');
      }

      const hasName =
        (sidebar.getAttribute('aria-label')?.trim() || '') !== '' ||
        (sidebar.getAttribute('aria-labelledby')?.trim() || '') !== '';

      if (!hasName) {
        const heading = sidebar.querySelector(
          'h1,h2,h3,h4,h5,h6,.p-sidebar-header,.p-dialog-title'
        );
        if (heading) {
          if (!heading.id) heading.id = `aside-${Math.random().toString(36).slice(2, 8)}`;
          sidebar.setAttribute('aria-labelledby', heading.id);
        } else {
          const isNav = sidebar.getAttribute('role') === 'navigation';
          sidebar.setAttribute('aria-label', isNav ? 'Sidebar navigation' : 'Sidebar');
        }
      }
    });
  }

  // fix PrimeNG dropdowns
  private fixDropdownRoles(element: HTMLElement): void {
    const wrappers = element.querySelectorAll('.p-dropdown-items-wrapper');

    wrappers.forEach(wrapper => {
      if (wrapper.getAttribute('role') === 'listbox') {
        wrapper.removeAttribute('role');
        wrapper.setAttribute('role', 'presentation');
      }

      if (!wrapper.hasAttribute('tabindex')) {
        wrapper.setAttribute('tabindex', '0');
      }

      const ul = wrapper.querySelector('ul');
      if (ul && !ul.hasAttribute('role')) {
        ul.setAttribute('role', 'listbox');
      }

      const liElements = ul?.querySelectorAll('li') || [];
      liElements.forEach(li => {
        if (!li.hasAttribute('role')) {
          li.setAttribute('role', 'option');
        }
      });
    });
  }

  // fixed missing landmarks
  private fixMissingLandmarks(el: HTMLElement): void {
    let label: string | null = null;
    let targetEl: HTMLElement = el;

    if (
      el.matches(
        'p-fieldset, p-toolbar, p-panel, p-sidebar, p-card, p-tabview, p-tabmenu, p-paginator, .app-content-container, .content-section, .p-datatable-wrapper, app-routing-statistic-summary, .p-overlay, .p-datepicker-calendar-container, .p-hour-picker, .p-separator, .p-minute-picker, .p-ampm-picker, .flatpickr-months, .flatpickr-innerContainer'
      )
    ) {
      label = el.textContent?.trim() || el.tagName.toLowerCase().replace('p-', '') + ' component';
      targetEl = el;
    } else if (
      el.matches('p-listbox, p-multiselect, p-dropdown') ||
      el.closest('p-listbox, p-multiselect, p-dropdown')
    ) {
      const wrapper = el.closest('p-listbox, p-multiselect, p-dropdown') as HTMLElement | null;
      if (wrapper) {
        label =
          wrapper.textContent?.trim() ||
          wrapper.tagName.toLowerCase().replace('p-', '') + ' component';
        targetEl = wrapper;
      } else {
        return;
      }
    }

    if (
      targetEl.closest(
        'main,[role="main"],header,[role="banner"],footer,[role="contentinfo"],nav,[role="navigation"],aside,[role="complementary"],form,[role="search"],section[role="region"]'
      )
    ) {
      return;
    }

    if (
      el.matches('h5.mt-4.font-semibold') &&
      !el.closest('main,[role="main"],section[role="region"],header,footer')
    ) {
      const parent = el.parentElement;
      if (parent && !parent.hasAttribute('role')) {
        parent.setAttribute('role', 'region');
        parent.setAttribute('aria-label', 'Recent Authorizations');
        return;
      }
    }

    if (label && targetEl && !targetEl.closest('section[role="region"]')) {
      if (targetEl.matches('p-tabmenu') || targetEl.tagName.toLowerCase() === 'p-tabmenu') {
        targetEl.setAttribute('role', 'navigation');
        if (!targetEl.hasAttribute('aria-label'))
          targetEl.setAttribute('aria-label', 'Tab Navigation');
      } else {
        targetEl.setAttribute('role', 'region');
        if (!targetEl.hasAttribute('aria-label')) {
          targetEl.setAttribute('aria-label', `${label} ${++this.landmarkCounter}`);
        }
      }
    }
  }

  //fixed aria hidden focusable
  private fixAriaHiddenFocusable(el: HTMLElement): void {
    const isHidden = el.getAttribute('aria-hidden') === 'true';
    const focusable = el.querySelectorAll('button, [tabindex], input, select, textarea, a[href]');

    focusable.forEach(node => {
      if (isHidden) {
        node.setAttribute('tabindex', '-1');
        if (node instanceof HTMLButtonElement || node instanceof HTMLInputElement) {
          node.disabled = true;
        }
        if (node.tagName === 'A') {
          const href = node.getAttribute('href');
          if (href) node.setAttribute('data-original-href', href);
          node.removeAttribute('href');
        }
      } else {
        node.removeAttribute('tabindex');
        if (node instanceof HTMLButtonElement || node instanceof HTMLInputElement) {
          node.disabled = false;
        }
        if (node.tagName === 'A') {
          const originalHref = node.getAttribute('data-original-href');
          if (originalHref) node.setAttribute('href', originalHref);
        }
      }
    });
  }

  // fixed heading order
  private fixHeadingOrder(el: HTMLElement): void {
    if (el.hasAttribute('data-injected-h1')) return;
    if (el.matches('h1, h2, h3, h4, h5, h6')) {
      const previousHeading = el.previousElementSibling?.closest('h1,h2,h3,h4,h5,h6');
      let correctLevel = 2;
      if (previousHeading) {
        const prevLevel = parseInt(previousHeading.tagName.replace('H', ''), 10);
        correctLevel = Math.min(prevLevel + 1, 6);
      }

      el.setAttribute('role', 'heading');
      el.setAttribute('aria-level', correctLevel.toString());
    }
  }

  // made unique navigation landmarks
  private ensureUniqueNavigationLandmarks(root: HTMLElement): void {
    const navs = this.document.querySelectorAll('nav');
    const seenLabels: Set<string> = new Set();

    navs.forEach((nav, index) => {
      const el = nav as HTMLElement;

      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'navigation');
      }

      let label = el.getAttribute('aria-label') || '';
      if (!label || seenLabels.has(label)) {
        label = index === 0 ? 'Primary navigation' : `Navigation section ${index + 1}`;
        el.setAttribute('aria-label', label);
      }

      seenLabels.add(label);
    });
  }

  // fixed generic inputs
  private fixGenericInputs(el: HTMLElement): void {
    if (el.tagName === 'INPUT') {
      const name = el.getAttribute('formcontrolname') || el.getAttribute('name') || 'input field';
      if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', name);
      const id = el.id || `input-${Math.random().toString(36).substring(2, 8)}`;
      el.id = id;
      const label = el.closest('.field')?.querySelector('label');
      if (label) {
        if (!label.hasAttribute('for')) label.setAttribute('for', id);
        el.setAttribute('aria-labelledby', label.textContent?.trim() || name);
      }
    }
  }

  // fixed alternate text
  private fixTextAlternatives(el: HTMLElement): void {
    if (el.tagName === 'IMG' && !el.hasAttribute('alt')) {
      el.setAttribute('alt', 'decorative');
    }
    if ((el.tagName === 'BUTTON' || el.tagName === 'A') && el.textContent?.trim() === '') {
      el.setAttribute('aria-label', 'action');
    }
  }

  // validated aria-activedescendant
  private fixInvalidAriaActivedescendant(el: HTMLElement): void {
    if (el.hasAttribute('aria-activedescendant')) {
      const targetId = el.getAttribute('aria-activedescendant')!;
      if (!this.document.getElementById(targetId)) {
        el.removeAttribute('aria-activedescendant');
      }
    }
  }

  // fixed table headers
  private fixEmptyOrGenericTableHeaders(element: HTMLElement): void {
    const headers = element.querySelectorAll('th');
    headers.forEach((th, index) => {
      const text = th.textContent?.trim();
      const ariaLabel = th.getAttribute('aria-label')?.trim();

      if (!th.hasAttribute('scope')) {
        th.setAttribute('scope', 'col');
      }

      if (!text && !ariaLabel) {
        const span = document.createElement('span');
        span.className = 'sr-only';
        span.textContent = th.getAttribute('data-field') || `Column ${index + 1}`;
        th.appendChild(span);
      } else if (!text && ariaLabel) {
        const span = document.createElement('span');
        span.className = 'sr-only';
        span.textContent = ariaLabel;
        th.appendChild(span);
      }
    });
  }

  // validated aria-required
  private fixInvalidAriaRequired(el: HTMLElement): void {
    if (el.hasAttribute('aria-required')) {
      const role = el.getAttribute('role') || '';
      const allowedRoles = ['radiogroup', 'combobox', 'textbox', 'listbox'];
      if (!allowedRoles.includes(role)) {
        const hasRadioButtons = el.querySelector('input[type="radio"]') !== null;
        if (hasRadioButtons) {
          el.setAttribute('role', 'radiogroup');
        } else {
          el.removeAttribute('aria-required');
        }
      }
    }
  }

  // fixed scrollable containers
  private fixScrollableContainers(el: HTMLElement): void {
    const role = el.getAttribute('role');
    if (role === 'presentation' || role === 'none') {
      return;
    }

    if (
      ['menubar', 'menu', 'tablist', 'listbox', 'grid grid-cols-12 gap-4', 'tree', 'combobox'].includes(role || '')
    ) {
      return;
    }

    if (el.classList.contains('sdoh-referral-timeline-container')) {
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        el.setAttribute('aria-label', 'Referral Timeline');
      }
      return;
    }

    if (el.classList.contains('comments-container')) {
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      if (!el.hasAttribute('role')) el.setAttribute('role', 'region');
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        const heading = el.querySelector(
          'h1,h2,h3,h4,h5,h6,[role="heading"]'
        ) as HTMLElement | null;
        if (heading) {
          if (!heading.id) heading.id = `comments-title-${Math.random().toString(36).slice(2, 8)}`;
          el.setAttribute('aria-labelledby', heading.id);
        } else {
          el.setAttribute('aria-label', `Comments ${++this.landmarkCounter}`);
        }
      }
      return;
    }

    const isScrollable = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
    if (isScrollable && el.tagName.toLowerCase() === 'app-comments') {
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');

      if (!el.hasAttribute('role')) el.setAttribute('role', 'region');

      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        const heading = el.querySelector(
          'h1, h2, h3, h4, h5, h6, [role="heading"]'
        ) as HTMLElement | null;
        if (heading) {
          if (!heading.id) heading.id = `comments-title-${Math.random().toString(36).slice(2, 8)}`;
          el.setAttribute('aria-labelledby', heading.id);
        } else {
          el.setAttribute('aria-label', `Comments ${++this.landmarkCounter}`);
        }
      }
      return;
    }

    if (
      isScrollable &&
      (el.classList.contains('p-datatable-wrapper') || el.classList.contains('ng-sidebar--left'))
    ) {
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }

      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'region');
      }

      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        const baseLabel = el.classList.contains('p-datatable-wrapper')
          ? 'Scrollable data table'
          : 'Scrollable sidebar';

        this.landmarkCounter++;
        el.setAttribute('aria-label', `${baseLabel} ${this.landmarkCounter}`);
      }

      el.style.outline = 'none';
    }
  }
}
